<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 80px)' : '1200px'"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Declaration of Conformity</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          class="mr-2"
          @click="showPreview = false"
        >
          <v-icon>mdi-eye-off</v-icon>
        </v-btn>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="conformity-dialog-layout">
          <div :class="['conformity-form-pane', showPreview ? 'with-preview' : '']">
            <div class="pa-6">
              <div class="section-title mb-2">Manufacturer Information</div>

              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.manufacturer_name"
                    label="Manufacturer Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('manufacturer_name')"
                    @input="clearValidationError('manufacturer_name')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.manufacturer_address_line_1"
                    label="Address Line 1 *"
                    outlined
                    dense
                    :error-messages="getValidationError('manufacturer_address_line_1')"
                    @input="clearValidationError('manufacturer_address_line_1')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.manufacturer_address_line_2"
                    label="Address Line 2 *"
                    outlined
                    dense
                    :error-messages="getValidationError('manufacturer_address_line_2')"
                    @input="clearValidationError('manufacturer_address_line_2')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Buyer Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.buyer_name"
                    label="Buyer Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('buyer_name')"
                    @input="clearValidationError('buyer_name')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.buyer_tax_number"
                    label="Buyer Tax Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('buyer_tax_number') }"
                    @input="clearOptionalWarning('buyer_tax_number')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.buyer_address"
                    label="Buyer Address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('buyer_address')"
                    @input="clearValidationError('buyer_address')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Document Data</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.invoice_number"
                    label="Invoice Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('invoice_number') }"
                    @input="clearOptionalWarning('invoice_number')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="issueDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.issue_date"
                        label="Issue Date *"
                        readonly
                        outlined
                        dense
                        :error-messages="getValidationError('issue_date')"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.issue_date"
                      no-title
                      scrollable
                      @input="onIssueDateSelected"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.standards"
                    label="Standards *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('standards')"
                    @input="clearValidationError('standards')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.compliance_statement"
                    label="Compliance Statement"
                    outlined
                    dense
                    rows="3"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('compliance_statement') }"
                    @input="clearOptionalWarning('compliance_statement')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.eu_origin_statement"
                    label="EU Manufacturing Statement"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('eu_origin_statement') }"
                    @input="clearOptionalWarning('eu_origin_statement')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="form.signature_name"
                    label="Signature Name / Company Name"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('signature_name') }"
                    @input="clearOptionalWarning('signature_name')"
                  />
                </v-col>
              </v-row>
            </div>
          </div>

          <div v-if="showPreview" class="conformity-preview-pane">
            <div class="conformity-preview-toolbar">
              <div class="subtitle-2 font-weight-medium">Preview</div>

              <v-progress-circular
                v-if="previewLoading"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
            </div>

            <iframe
              v-if="previewHtml"
              :srcdoc="previewHtml"
              class="conformity-preview-frame"
              frameborder="0"
            />

            <div v-else class="conformity-preview-empty">
              No preview available.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          text
          color="primary"
          :loading="previewLoading"
          @click="openPreview"
        >
          {{ showPreview ? "Hide Preview" : "Open Preview" }}
        </v-btn>

        <v-spacer />

        <v-btn text @click="closeDialog">Cancel</v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="generateDocument"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";


const DOCUMENT_TYPE = "declaration_of_conformity";

export default {
  name: "DeclarationOfConformityDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    salesOrderId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      previewLoading: false,
      showPreview: false,
      previewHtml: "",
      previewDebounceTimer: null,
      previewRequestId: 0,
      issueDateMenu: false,
      validationErrors: {},
      optionalWarnings: {},
      form: {
        manufacturer_name: "",
        manufacturer_address_line_1: "",
        manufacturer_address_line_2: "",
        buyer_name: "",
        buyer_address: "",
        buyer_tax_number: "",
        invoice_number: "",
        standards: "",
        compliance_statement: "",
        eu_origin_statement: "",
        issue_date: "",
        signature_name: "",
      },
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadDefaults();
      } else {
        this.resetPreview();
      }
    },

    form: {
      deep: true,
      handler() {
        if (!this.showPreview) {
          return;
        }

        this.schedulePreviewRefresh();
      },
    },
  },

  methods: {
    async loadDefaults() {
      try {
        const { data } = await ApiService.get(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/defaults`
        );

        this.form = {
          manufacturer_name: data.manufacturer_name || "",
          manufacturer_address_line_1: data.manufacturer_address_line_1 || "",
          manufacturer_address_line_2: data.manufacturer_address_line_2 || "",
          buyer_name: data.buyer_name || "",
          buyer_address: data.buyer_address || "",
          buyer_tax_number: data.buyer_tax_number || "",
          invoice_number: data.invoice_number || "",
          standards: data.standards || "",
          compliance_statement: data.compliance_statement || "",
          eu_origin_statement: data.eu_origin_statement || "",
          issue_date: data.issue_date || "",
          signature_name: data.signature_name || "",
        };

        this.validationErrors = {};
        this.optionalWarnings = {};
      } catch (e) {
        console.log(e);
      }
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    hasOptionalWarning(field) {
      return Boolean(this.optionalWarnings[field]);
    },

    clearOptionalWarning(field) {
      if (this.optionalWarnings[field]) {
        this.$delete(this.optionalWarnings, field);
      }
    },

    getValidationError(field) {
      return this.validationErrors[field] || [];
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },

    validateForm() {
      const errors = {};
      const requiredFields = [
        { key: "manufacturer_name", label: "Manufacturer Name" },
        { key: "manufacturer_address_line_1", label: "Address Line 1" },
        { key: "manufacturer_address_line_2", label: "Address Line 2" },
        { key: "buyer_name", label: "Buyer Name" },
        { key: "buyer_address", label: "Buyer Address" },
        { key: "issue_date", label: "Issue Date" },
        { key: "standards", label: "Standards" },
      ];

      requiredFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          errors[field.key] = [`${field.label} is required.`];
        }
      });

      this.validationErrors = errors;

      return Object.keys(errors).length === 0;
    },

    async showValidationAlert() {
      await Swal.fire({
        title: "Missing required data",
        icon: "warning",
        text: "Please fill in all required fields before creating the document.",
        confirmButtonText: "OK",
      });
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalFields = [
        { key: "buyer_tax_number", label: "Buyer Tax Number" },
        { key: "invoice_number", label: "Invoice Number" },
        { key: "compliance_statement", label: "Compliance Statement" },
        { key: "eu_origin_statement", label: "EU Manufacturing Statement" },
        { key: "signature_name", label: "Signature Name / Company Name" },
      ];

      optionalFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          missingFields.push(field);
        }
      });

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, generate document",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    onIssueDateSelected() {
      this.issueDateMenu = false;
      this.clearValidationError("issue_date");
    },

    resetPreview() {
      this.showPreview = false;
      this.previewHtml = "";
      this.previewLoading = false;
      this.validationErrors = {};
      this.optionalWarnings = {};

      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
        this.previewDebounceTimer = null;
      }
    },

    closeDialog() {
      this.resetPreview();
      this.dialogProxy = false;
    },

    schedulePreviewRefresh() {
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }

      this.previewDebounceTimer = setTimeout(() => {
        this.refreshPreview();
      }, 400);
    },

    async openPreview() {
      if(this.showPreview === true){
        this.showPreview = false;
      }else{
        this.showPreview = true;
      }
      
      await this.refreshPreview();
    },

    async refreshPreview() {
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/preview`,
          this.form
        );

        if (requestId !== this.previewRequestId) {
          return;
        }

        this.previewHtml = data.html || "";
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async generateDocument() {
      if (!this.validateForm()) {
        await this.showValidationAlert();
        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);


      const canContinue = await this.confirmMissingOptionalFields();

      if (!canContinue) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/generate`,
          this.form
        );

        if (data && data.url) {
          window.open(data.url, "_blank");
        }

        this.$emit("generated", data);
        this.closeDialog();
      } catch (error) {
        console.error(error);

        Swal.fire({
          title: "Error",
          icon: "error",
          text: "The document could not be created.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.conformity-dialog-layout {
  display: flex;
  min-height: 70vh;
}

.conformity-form-pane {
  flex: 0 0 100%;
  max-width: 100%;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}
.conformity-form-pane.with-preview {
  flex: 0 0 40%;
  max-width: 40%;
}

.conformity-preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 16px;
  min-width: 0;
}

.conformity-preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.conformity-preview-frame {
  width: 100%;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
}

.conformity-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  color: #757575;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
}

.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}
</style>